/**
 * FeeRequestSection — cleaner-side section on TaskDetailPage for
 * filing an additional-fee request and viewing the rows already
 * filed against this task.
 *
 * Renders the existing requests with their current status + denial
 * reason. When the task is in a workable state (pending / in_progress)
 * the cleaner who owns the task sees a "+ Request additional fee"
 * button that toggles an inline form (amount, reason).
 *
 * Photo attachment to fee requests is NOT yet a separate uploader
 * here — paths can be wired later from the existing task PhotoUploader.
 * The schema column ships in #76 so this surface can grow into it
 * without a migration in the next iteration.
 */

import { useState, type ReactNode } from 'react'
import { useTranslation }            from 'react-i18next'
import { useRequestAdditionalFee }   from '@/hooks/useRequestAdditionalFee'
import {
  useAdditionalFeeRequests,
  type AdditionalFeeRequestRow,
  type AdditionalFeeStatus,
}                                    from '@/hooks/useAdditionalFeeRequests'
import { useTenantFeatures }         from '@/hooks/useTenantFeatures'
import { isAppError }                from '@/lib/errors/AppError'

interface Props {
  taskId:        string
  taskStatus:    string
  /** True only on the cleaner's own task view — drives the request form. */
  canRequest:    boolean
}

export function FeeRequestSection({ taskId, taskStatus, canRequest }: Props): ReactNode {
  const { t } = useTranslation()
  const { has: hasFeature, isLoading: flagsLoading } = useTenantFeatures()
  const { data, isLoading } = useAdditionalFeeRequests({ cleaningTaskId: taskId })

  // Tenant feature gate: tenants whose contract does not include the
  // exceedance-fee clause never see this surface. Defaults closed
  // while the flag-fetch is in flight to avoid flashing a UI the
  // tenant cannot actually use.
  if (flagsLoading) return null
  if (!hasFeature('additional_fee_workflow')) return null

  const requests = data ?? []
  const showForm = canRequest && (taskStatus === 'pending' || taskStatus === 'in_progress')

  if (!showForm && requests.length === 0 && !isLoading) return null

  return (
    <section
      data-testid="fee-request-section"
      aria-labelledby="fee-request-heading"
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-lg)',
        padding:      '1rem',
        display:      'flex',
        flexDirection: 'column',
        gap:          '0.6rem',
      }}
    >
      <h2 id="fee-request-heading" style={{ margin: 0, fontSize: '1rem' }}>
        {t('feeRequest.heading', 'Additional fee requests')}
      </h2>

      {requests.length > 0 && (
        <ul data-testid="fee-request-row-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {requests.map((r) => <ExistingRow key={r.id} row={r} />)}
        </ul>
      )}

      {showForm && <RequestForm taskId={taskId} />}
    </section>
  )
}

function ExistingRow({ row }: { row: AdditionalFeeRequestRow }): ReactNode {
  return (
    <li
      data-testid="fee-request-existing"
      data-request-id={row.id}
      data-status={row.status}
      style={{
        display:       'flex',
        gap:           '0.5rem',
        padding:       '0.4rem 0',
        borderBottom:  '1px solid var(--brand-neutral-100)',
        alignItems:    'baseline',
      }}
    >
      <strong style={{ fontSize: '0.95rem' }}>${(row.requested_amount_cents / 100).toFixed(2)}</strong>
      <StatusChip status={row.status} />
      <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.85rem', flex: 1 }}>
        {row.reason}
      </span>
    </li>
  )
}

function StatusChip({ status }: { status: AdditionalFeeStatus }): ReactNode {
  return (
    <span
      data-testid="fee-request-status-chip"
      data-status={status}
      style={{
        display:       'inline-block',
        padding:       '0.1rem 0.45rem',
        borderRadius:  'var(--brand-radius-full)',
        background:    'var(--brand-neutral-100)',
        color:         'var(--brand-neutral-700)',
        fontSize:      '0.7rem',
        fontWeight:    600,
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function RequestForm({ taskId }: { taskId: string }): ReactNode {
  const { t } = useTranslation()
  const request = useRequestAdditionalFee()
  const [open, setOpen]                   = useState(false)
  const [amountDollars, setAmountDollars] = useState('')
  const [reason, setReason]               = useState('')

  function parseAmountCents(): number | null {
    const trimmed = amountDollars.trim()
    if (trimmed.length === 0) return null
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed) || parsed < 0) return null
    return Math.round(parsed * 100)
  }

  async function submit(): Promise<void> {
    const cents = parseAmountCents()
    if (cents === null) return
    try {
      await request.mutateAsync({
        cleaningTaskId:       taskId,
        requestedAmountCents: cents,
        reason:               reason.trim(),
      })
      setOpen(false)
      setAmountDollars('')
      setReason('')
    } catch { /* error rendered via request.error */ }
  }

  const errMsg = request.error && isAppError(request.error) ? request.error.userMessage : null
  const amountValid = parseAmountCents() !== null
  const reasonValid = reason.trim().length > 0 && reason.length <= 1000

  if (!open) {
    return (
      <button
        type="button"
        data-testid="fee-request-open"
        onClick={() => setOpen(true)}
        style={{
          alignSelf:    'flex-start',
          padding:      '0.4rem 0.8rem',
          background:   'transparent',
          color:        'var(--brand-primary)',
          border:       '1px dashed var(--brand-primary)',
          borderRadius: 'var(--brand-radius-sm)',
          fontSize:     '0.85rem',
          cursor:       'pointer',
        }}
      >
        {t('feeRequest.openForm', '+ Request additional fee')}
      </button>
    )
  }

  return (
    <div
      data-testid="fee-request-form"
      style={{
        display:        'flex',
        flexDirection:  'column',
        gap:            '0.5rem',
        padding:        '0.6rem',
        background:     'var(--brand-neutral-50, var(--brand-neutral-100))',
        borderRadius:   'var(--brand-radius-sm)',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
          {t('feeRequest.amountLabel', 'Amount (USD)')}
        </span>
        <input
          data-testid="fee-request-amount-input"
          type="number"
          inputMode="decimal"
          min={0}
          max={10000}
          step="0.01"
          value={amountDollars}
          onChange={(e) => setAmountDollars(e.target.value)}
          style={{
            padding:      '0.4rem 0.5rem',
            fontSize:     '0.9rem',
            borderRadius: 'var(--brand-radius-sm)',
            border:       '1px solid var(--brand-neutral-300)',
          }}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
          {t('feeRequest.reasonLabel', 'Reason (1–1000 chars)')}
        </span>
        <textarea
          data-testid="fee-request-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 1000))}
          rows={3}
          maxLength={1000}
          style={{
            fontSize:     '0.9rem',
            padding:      '0.4rem',
            borderRadius: 'var(--brand-radius-sm)',
            border:       '1px solid var(--brand-neutral-300)',
            resize:       'vertical',
          }}
        />
      </label>
      <div style={{ display: 'flex', gap: '0.4rem' }}>
        <button
          type="button"
          data-testid="fee-request-submit"
          onClick={() => { void submit() }}
          disabled={request.isPending || !amountValid || !reasonValid}
          style={{
            padding:      '0.4rem 0.8rem',
            background:   request.isPending || !amountValid || !reasonValid
                            ? 'var(--brand-neutral-300)'
                            : 'var(--brand-primary)',
            color:        'var(--brand-white)',
            border:       'none',
            borderRadius: 'var(--brand-radius-sm)',
            fontSize:     '0.85rem',
            fontWeight:   600,
            cursor:       request.isPending || !amountValid || !reasonValid ? 'not-allowed' : 'pointer',
          }}
        >
          {t('feeRequest.submit', 'File request')}
        </button>
        <button
          type="button"
          data-testid="fee-request-cancel"
          onClick={() => { setOpen(false); setAmountDollars(''); setReason('') }}
          style={{
            padding:      '0.4rem 0.8rem',
            background:   'transparent',
            color:        'var(--brand-neutral-700)',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-sm)',
            fontSize:     '0.85rem',
            cursor:       'pointer',
          }}
        >
          {t('feeRequest.cancel', 'Cancel')}
        </button>
      </div>
      {errMsg && (
        <p role="alert" data-testid="fee-request-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {errMsg}
        </p>
      )}
    </div>
  )
}
